import type { AgreementBillingPeriod } from '@/types/agreement-terms';
import type { AgreementTermMonths } from '@/lib/constants/agreement-term-months';
import { POS_SYSTEM_OPTIONS, type PosSystemOption } from '@/lib/constants/pos-systems';

export type SlaAgreementStatus =
  | 'draft'
  | 'generated'
  | 'signature_requested'
  | 'customer_viewed'
  | 'customer_in_progress'
  | 'revision_requested'
  | 'customer_signed'
  | 'awaiting_tpb_signature'
  | 'expired'
  | 'superseded'
  | 'sent_external'
  | 'signed_uploaded'
  | 'completed'
  | 'voided';

export type SlaAgreementType = 'master_agreement';
export type SlaAgreementSourceType = 'deal' | 'quote' | 'invoice' | 'location';
export type SlaAgreementSendPackageStatus = 'created' | 'sent' | 'send_failed' | 'revoked' | 'expired' | 'superseded';
export type SlaAgreementSignerRole = 'customer' | 'tpb';
export type SlaAgreementSignerStatus =
  | 'pending'
  | 'emailed'
  | 'viewed'
  | 'in_progress'
  | 'revision_requested'
  | 'signed'
  | 'revoked'
  | 'expired'
  | 'superseded';
export type SlaAgreementSignerTokenStatus = 'active' | 'revoked' | 'expired' | 'superseded';
export type SlaAgreementEventActorType = 'system' | 'user';
export type SlaAgreementSignatureType = 'typed';
export type SlaAgreementRevisionRequestStatus = 'open' | 'resolved' | 'superseded';
export type SlaAgreementSigningSessionError =
  | 'invalid_token'
  | 'revoked'
  | 'expired'
  | 'voided'
  | 'superseded'
  | 'already_signed'
  | 'validation_error';
export type SlaSigningViewportBucket = 'desktop' | 'tablet' | 'phone';

export interface SlaAgreementHardwareRow {
  id: string;
  line_number: number;
  description: string;
  quantity: number;
  unit_price: number;
  line_total: number;
  display_type_id: string | null;
}

export interface SlaAgreementStoreDraft {
  id: string;
  expected_location_id: string | null;
  name: string | null;
  address: string | null;
  tentative_installation_date: string | null;
}

export interface SlaAgreementDraftPayload {
  source_type: SlaAgreementSourceType | null;
  source_reference: string | null;
  source_quote_number: string | null;
  customer_name: string | null;
  legal_entity_name: string | null;
  primary_location_name: string | null;
  primary_location_address: string | null;
  signer_name: string | null;
  signer_title: string | null;
  signer_email: string | null;
  notice_address: string | null;
  pos_system: string | null;
  contract_term_months: AgreementTermMonths;
  software_license_fee_amount: number | null;
  software_license_fee_period: AgreementBillingPeriod | null;
  stores: SlaAgreementStoreDraft[];
  hardware_schedule_rows: SlaAgreementHardwareRow[];
}

export interface SlaAgreementStoreMapping {
  store_id: string;
  store_name: string;
  store_address: string | null;
  tentative_installation_date: string | null;
  location_id: string | null;
}

export interface SlaAgreementCustomerSubmission {
  execution_date: string | null;
  legal_entity_name: string;
  pos_system: string | null;
  signer_name: string;
  signer_title: string;
  signer_email: string;
  notice_address: string;
  customer_initials: string | null;
  customer_initial_date: string | null;
  stores: SlaAgreementStoreMapping[];
  signature_type: SlaAgreementSignatureType;
  signature_text: string;
  consent_accepted: boolean;
}

export const SLA_SIGNING_POS_SYSTEM_OPTIONS = POS_SYSTEM_OPTIONS;
export type SlaSigningEditablePosSystem = PosSystemOption;

export const SLA_ALLOWED_CUSTOMER_FIELDS = [
  'execution_date',
  'legal_entity_name',
  'pos_system',
  'signer_name',
  'signer_title',
  'signer_email',
  'notice_address',
  'store_name',
  'store_address',
  'tentative_installation_date',
  'customer_initials',
  'customer_initial_date',
] as const;

export type SlaAllowedCustomerField = (typeof SLA_ALLOWED_CUSTOMER_FIELDS)[number];
export type SlaAgreementSigningFormState = Omit<SlaAgreementCustomerSubmission, 'signature_type'>;

export const SLA_SIGNING_UI_EVENT_TYPES = [
  'agreement_signing_draft_restored',
  'agreement_signing_draft_discarded',
  'agreement_signing_submit_blocked_client',
  'agreement_signing_field_invalid_blurred',
  'agreement_signing_revision_panel_opened',
  'agreement_signing_first_field_interacted',
  'agreement_signing_submit_succeeded_client',
  'agreement_signing_revision_requested_client',
] as const;

export type SlaSigningUiEventType = (typeof SLA_SIGNING_UI_EVENT_TYPES)[number];

export interface SlaSigningUiEventDetails {
  fieldId?: string;
  blockedRequiredCount?: number;
  viewportBucket?: SlaSigningViewportBucket;
  elapsedMs?: number;
  draftRestored?: boolean;
}

export interface LogSlaSigningUiEventInput {
  token: string;
  eventType: SlaSigningUiEventType;
  details?: SlaSigningUiEventDetails | null;
}

export interface LogSlaSigningUiEventResponse {
  ok: boolean;
  error?: SlaAgreementSigningSessionError;
  message?: string;
  agreementId?: string;
  eventType?: SlaSigningUiEventType;
}

export interface SlaAgreementReconciliation {
  execution_date: string | null;
  legal_entity_name: string;
  pos_system: string | null;
  signer_name: string;
  signer_title: string;
  signer_email: string;
  notice_address: string;
  customer_initials: string | null;
  customer_initial_date: string | null;
  stores: SlaAgreementStoreMapping[];
}

export interface SlaAgreementWritebackLocationResult {
  location_id: string;
  applied_fields: string[];
}

export interface SlaAgreementWritebackContactResult {
  location_id: string;
  action: 'created' | 'updated' | 'skipped';
  contact_id: string | null;
  note: string | null;
}

export interface SlaAgreementWritebackEquipmentResult {
  mode:
    | 'single_store_applied'
    | 'single_store_skipped_existing'
    | 'single_store_no_candidates'
    | 'multi_store_skipped';
  order_id: string | null;
  created_display_type_ids: string[];
  skipped_display_type_ids: string[];
  note: string | null;
}

export interface SlaAgreementWritebackResult {
  location_updates: SlaAgreementWritebackLocationResult[];
  contact_updates: SlaAgreementWritebackContactResult[];
  equipment: SlaAgreementWritebackEquipmentResult | null;
  warnings: string[];
}

export interface SlaAgreementSignerToken {
  id: string;
  agreement_id: string;
  send_package_id: string;
  signer_id: string;
  status: SlaAgreementSignerTokenStatus;
  token_hash: string;
  expires_at: string;
  revoked_at: string | null;
  expired_at: string | null;
  superseded_at: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
}

export interface SlaAgreementSigner {
  id: string;
  agreement_id: string;
  send_package_id: string;
  role: SlaAgreementSignerRole;
  status: SlaAgreementSignerStatus;
  internal_user_id: string | null;
  reminder_count: number;
  first_reminder_sent_at: string | null;
  last_reminder_sent_at: string | null;
  name: string | null;
  title: string | null;
  email: string | null;
  emailed_at: string | null;
  viewed_at: string | null;
  started_at: string | null;
  consented_at: string | null;
  signed_at: string | null;
  submission_json: SlaAgreementCustomerSubmission | null;
  signature_type: SlaAgreementSignatureType | null;
  signature_text: string | null;
  last_ip_address: string | null;
  last_user_agent: string | null;
  revoked_at: string | null;
  expired_at: string | null;
  superseded_at: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
  tokens: SlaAgreementSignerToken[];
}

export interface SlaAgreementSendPackage {
  id: string;
  agreement_id: string;
  package_version: number;
  status: SlaAgreementSendPackageStatus;
  agreement_number: string;
  template_key: string;
  template_version: string;
  generated_file_path: string;
  draft_payload_json: SlaAgreementDraftPayload;
  email_id: string | null;
  sent_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  expired_at: string | null;
  superseded_at: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
  signers: SlaAgreementSigner[];
}

export interface SlaAgreementEvent {
  id: string;
  agreement_id: string;
  send_package_id: string | null;
  signer_id: string | null;
  token_id: string | null;
  package_version: number | null;
  actor_type: SlaAgreementEventActorType;
  actor_user_id: string | null;
  actor_label: string | null;
  event_type: string;
  details_json: Record<string, unknown> | null;
  created_at: string;
}

export interface SlaAgreementRevisionRequest {
  id: string;
  agreement_id: string;
  send_package_id: string;
  signer_id: string;
  status: SlaAgreementRevisionRequestStatus;
  message: string;
  created_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
}

export interface SlaAgreementSigningSession {
  agreementId: string;
  agreementNumber: string;
  sendPackageId: string;
  signerId: string;
  signer: {
    name: string;
    email: string;
    title: string | null;
  };
  draftPayload: SlaAgreementDraftPayload;
  reconciliation: SlaAgreementReconciliation | null;
  templateKey: string;
  templateVersion: string;
  allowedCustomerFields: SlaAllowedCustomerField[];
  canRequestRevision: boolean;
  canSign: boolean;
}

export interface OpenSlaSigningSessionResponse {
  ok: boolean;
  error?: SlaAgreementSigningSessionError;
  session?: SlaAgreementSigningSession;
}

export interface SubmitSlaCustomerSignatureResponse {
  ok: boolean;
  error?: SlaAgreementSigningSessionError;
  message?: string;
  agreementId?: string;
  status?: SlaAgreementStatus;
  alreadySigned?: boolean;
}

export interface RequestSlaSignatureRevisionResponse {
  ok: boolean;
  error?: SlaAgreementSigningSessionError;
  message?: string;
  agreementId?: string;
  status?: SlaAgreementStatus;
}

export interface SlaAgreement {
  id: string;
  agreement_number: string;
  agreement_type: SlaAgreementType;
  status: SlaAgreementStatus;
  template_key: string;
  template_version: string;
  quote_id: string | null;
  invoice_id: string | null;
  deal_id: string | null;
  primary_location_id: string | null;
  draft_payload_json: SlaAgreementDraftPayload;
  reconciliation_json: SlaAgreementReconciliation | null;
  writeback_result_json: SlaAgreementWritebackResult | null;
  current_send_package_id: string | null;
  generated_file_path: string | null;
  signed_file_path: string | null;
  finalized_file_path: string | null;
  generated_at: string | null;
  signature_requested_at: string | null;
  signature_request_expires_at: string | null;
  signature_request_revoked_at: string | null;
  signature_request_email_id: string | null;
  sent_at: string | null;
  signed_uploaded_at: string | null;
  finalized_at: string | null;
  completed_at: string | null;
  completed_delivery_at: string | null;
  completed_delivery_email_id: string | null;
  voided_at: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
}

export interface SlaAgreementInsert {
  agreement_number?: string;
  agreement_type?: SlaAgreementType;
  status?: SlaAgreementStatus;
  template_key?: string;
  template_version?: string;
  quote_id?: string | null;
  invoice_id?: string | null;
  deal_id?: string | null;
  primary_location_id?: string | null;
  draft_payload_json: SlaAgreementDraftPayload;
  reconciliation_json?: SlaAgreementReconciliation | null;
  writeback_result_json?: SlaAgreementWritebackResult | null;
  current_send_package_id?: string | null;
  generated_file_path?: string | null;
  signed_file_path?: string | null;
  finalized_file_path?: string | null;
  generated_at?: string | null;
  signature_requested_at?: string | null;
  signature_request_expires_at?: string | null;
  signature_request_revoked_at?: string | null;
  signature_request_email_id?: string | null;
  sent_at?: string | null;
  signed_uploaded_at?: string | null;
  finalized_at?: string | null;
  completed_at?: string | null;
  completed_delivery_at?: string | null;
  completed_delivery_email_id?: string | null;
  voided_at?: string | null;
}

export interface SlaAgreementUpdate {
  agreement_number?: string;
  agreement_type?: SlaAgreementType;
  status?: SlaAgreementStatus;
  template_key?: string;
  template_version?: string;
  quote_id?: string | null;
  invoice_id?: string | null;
  deal_id?: string | null;
  primary_location_id?: string | null;
  draft_payload_json?: SlaAgreementDraftPayload;
  reconciliation_json?: SlaAgreementReconciliation | null;
  writeback_result_json?: SlaAgreementWritebackResult | null;
  current_send_package_id?: string | null;
  generated_file_path?: string | null;
  signed_file_path?: string | null;
  finalized_file_path?: string | null;
  generated_at?: string | null;
  signature_requested_at?: string | null;
  signature_request_expires_at?: string | null;
  signature_request_revoked_at?: string | null;
  signature_request_email_id?: string | null;
  sent_at?: string | null;
  signed_uploaded_at?: string | null;
  finalized_at?: string | null;
  completed_at?: string | null;
  completed_delivery_at?: string | null;
  completed_delivery_email_id?: string | null;
  voided_at?: string | null;
}

export function isSlaAgreementActive(status: SlaAgreementStatus): boolean {
  return status !== 'voided' && status !== 'superseded';
}
